import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { BrandProvider } from "./contexts/BrandContext";
import { NotificationCountsProvider } from "./contexts/NotificationCountsContext";

import Login from "./pages/Login";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import Planning from "./pages/Planning";
import Collaborateurs from "./pages/Collaborateurs";
import Categories from "./pages/Categories";
import Indisponibilites from "./pages/Indisponibilites";
import Pointage from "./pages/Pointage";
import Marques from "./pages/Marques";
import MonDossier from "./pages/MonDossier";
import DossierCollaborateur from "./pages/DossierCollaborateur";
import DossierBeneficiaire from "./pages/DossierBeneficiaire";
import Sites from "./pages/Sites";
import ContactsBeneficiaires from "./pages/ContactsBeneficiaires";
import AgendaDepartements from "./pages/AgendaDepartements";
import BulletinsRemuneration from "./pages/BulletinsRemuneration";
import AuditLogs from "./pages/AuditLogs";
import Equipements from "./pages/Equipements";
import BibliothequeDocuments from "./pages/BibliothequeDocuments";
import HistoriqueVacances from "./pages/HistoriqueVacances";
import Notifications from "./pages/Notifications";
import KpiTransactions from "./pages/KpiTransactions";
import EspacePartenaire from "./pages/EspacePartenaire";
import EspaceAffiliation from "./pages/EspaceAffiliation";
import PartnerLayout from "./components/PartnerLayout";
import AffiliateLayout from "./components/AffiliateLayout";

import "./App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="flex h-screen items-center justify-center text-zinc-500" data-testid="auth-checking">
        Chargement…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Isolation stricte du rôle `partenaire` : quel que soit le chemin, un
// partenaire est renvoyé vers son espace personnel. Aucune autre page
// ne doit être accessible (le layout partenaire n'affiche d'ailleurs
// aucun menu).
function PartnerGate({ children }) {
  const { user } = useAuth();
  if (user?.role === "partenaire") {
    // Affilié (bénéficiaire commissions) → son espace dédié
    if (user?.partner_commission_beneficiary_id) {
      return <Navigate to="/espace-affiliation" replace />;
    }
    return <Navigate to="/espace-partenaire" replace />;
  }
  return children;
}

// Réciproquement : les autres rôles n'ont rien à faire sur /espace-partenaire.
function OnlyPartner({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "partenaire") {
    return <Navigate to="/" replace />;
  }
  return children;
}

// Espace affilié : seul un partenaire lié à un commission_beneficiary peut y accéder.
function OnlyAffiliate({ children }) {
  const { user } = useAuth();
  if (user && (user.role !== "partenaire" || !user.partner_commission_beneficiary_id)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

// Redirige vers "/" tout utilisateur dont le rôle n'est pas dans `roles`.
// Utilisé pour verrouiller les routes purement admin (ex. Bulletins de rémunération).
function RoleGuard({ roles, children }) {
  const { user } = useAuth();
  if (user && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

// Wrapper pour la consultation admin de l'espace affilié
function ConsultAffiliationWrapper() {
  return <EspaceAffiliation />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandProvider>
          <NotificationCountsProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/espace-partenaire"
              element={
                <ProtectedRoute>
                  <OnlyPartner>
                    <PartnerLayout />
                  </OnlyPartner>
                </ProtectedRoute>
              }
            >
              <Route index element={<EspacePartenaire />} />
            </Route>
            <Route
              path="/espace-affiliation"
              element={
                <ProtectedRoute>
                  <OnlyAffiliate>
                    <AffiliateLayout />
                  </OnlyAffiliate>
                </ProtectedRoute>
              }
            >
              <Route index element={<EspaceAffiliation />} />
            </Route>
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <PartnerGate>
                    <Layout />
                  </PartnerGate>
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="planning" element={<Planning />} />
              <Route path="collaborateurs" element={<Collaborateurs />} />
              <Route path="categories" element={<Categories />} />
              <Route path="conges" element={<Indisponibilites />} />
              <Route path="indisponibilites" element={<Indisponibilites />} />
              <Route path="pointage" element={<Pointage />} />
              <Route path="marques" element={<Marques />} />
              <Route path="mon-dossier" element={<MonDossier />} />
              <Route path="sites" element={<Sites />} />
              <Route path="contacts" element={<ContactsBeneficiaires />} />
              <Route path="agenda-departements" element={<AgendaDepartements />} />
              <Route path="bulletins-remuneration" element={<RoleGuard roles={["admin"]}><BulletinsRemuneration /></RoleGuard>} />
              <Route path="audit" element={<AuditLogs />} />
              <Route path="equipements" element={<Equipements />} />
              <Route path="bibliotheque-documents" element={<BibliothequeDocuments />} />
              <Route path="historique-vacances" element={<HistoriqueVacances />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="kpi-transactions" element={<KpiTransactions />} />
              <Route path="collaborateurs/:id/dossier" element={<DossierCollaborateur />} />
              <Route path="beneficiaires/:id/dossier" element={<DossierBeneficiaire />} />
              <Route
                path="consulter-partenaire/:bid"
                element={
                  <RoleGuard roles={["admin", "manager"]}>
                    <EspacePartenaire />
                  </RoleGuard>
                }
              />
              <Route
                path="consulter-affiliation/:bid"
                element={
                  <RoleGuard roles={["admin", "manager"]}>
                    <ConsultAffiliationWrapper />
                  </RoleGuard>
                }
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </NotificationCountsProvider>
        </BrandProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
